import { useState } from 'react';
import { Paperclip, Eye, Download } from 'lucide-react';
import {
  Button, Card, CardHeader, CardTitle, CardBody, FilePreviewDialog, IconButton,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { env } from '@/config/env';
import { formatDateTime } from '@/lib/format';
import type { ContractAttachment } from '@/api/contracts';

/* ─── helpers ─────────────────────────────────────────────────────────── */

function formatBytes(bytes?: number): string {
  if (!bytes || !isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Resolve the best available name from an attachment record. */
function attachmentName(att: ContractAttachment): string {
  return att.attachment_file_name || att.attachment_name || 'Untitled';
}

/**
 * Build a direct URL from `attachment_file_url` (storage-relative path).
 * Falls back to `attachment_url` if the canonical field is absent.
 */
function attachmentUrl(att: ContractAttachment): string | null {
  const path = att.attachment_file_url || att.attachment_url;
  if (!path) return null;
  // If the path is already a full URL, return it as-is.
  if (/^https?:\/\//.test(path)) return path;
  return `${env.storage.baseUrl}/${path}`;
}

/* ─── columns ─────────────────────────────────────────────────────────── */

function buildColumns(
  onPreview: (attachment: ContractAttachment) => void,
): ColumnDef<ContractAttachment, unknown>[] {
  return [
    {
      id: 'attachment_file_name',
      header: 'File name',
      cell: ({ row }) => {
        const name = attachmentName(row.original);
        const url = attachmentUrl(row.original);
        if (!url) return <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{name}</span>;
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-primary-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: 360,
            }}
            title={name}
          >
            {name}
          </a>
        );
      },
    },
    {
      id: 'attachment_file_size',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Size</span>,
      cell: ({ row }) => (
        <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 'var(--vn-font-size-sm)' }}>
          {formatBytes(row.original.attachment_file_size)}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Uploaded</span>,
      cell: ({ row }) => (
        <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const url = attachmentUrl(row.original);
        const name = attachmentName(row.original);
        if (!url) return null;
        return (
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label={`Preview ${name}`}
              onClick={() => onPreview(row.original)}
            >
              <Eye size={14} />
            </IconButton>
            <Button size="sm" variant="ghost" asChild>
              <a href={url} download={name} title="Download">
                <Download size={14} />
              </a>
            </Button>
          </div>
        );
      },
    },
  ];
}

/* ─── component ───────────────────────────────────────────────────────── */

interface ContractAttachmentsSectionProps {
  attachments: ContractAttachment[];
}

/**
 * Inline attachment section rendered below `ContractInfoCard` when the detail
 * record carries attachments. Data is already on the detail response — no
 * separate fetch. Mirrors the `ContractUpdatesLog` flat-Card section slot.
 */
export function ContractAttachmentsSection({ attachments }: ContractAttachmentsSectionProps) {
  // Index into the previewable attachments (those with a URL); null = closed.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const previewable = attachments.filter((a) => attachmentUrl(a));
  const current = previewIndex == null ? undefined : previewable[previewIndex];
  const currentUrl = current ? attachmentUrl(current) : null;
  const columns = buildColumns((att) => setPreviewIndex(previewable.indexOf(att)));

  return (
    <Card flat>
      <CardHeader>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <Paperclip size={15} />
          Attachments
        </CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={columns}
          data={attachments}
          getRowId={(row) => String(row.attachment_id ?? row.attachment_file_url ?? row.attachment_url ?? Math.random())}
          emptyState={
            <EmptyState
              icon={<Paperclip size={24} />}
              title="No attachments"
              description="No files are attached to this contract."
            />
          }
        />
      </CardBody>
      <FilePreviewDialog
        open={previewIndex != null}
        onOpenChange={(open) => { if (!open) setPreviewIndex(null); }}
        file={current && currentUrl ? { url: currentUrl, name: attachmentName(current) } : undefined}
        onDownload={currentUrl ? () => window.open(currentUrl, '_blank', 'noopener,noreferrer') : undefined}
        navigation={
          previewIndex != null && previewable.length > 1
            ? {
                index: previewIndex,
                total: previewable.length,
                onPrevious: () => setPreviewIndex((i) => (i == null ? i : Math.max(0, i - 1))),
                onNext: () =>
                  setPreviewIndex((i) => (i == null ? i : Math.min(previewable.length - 1, i + 1))),
              }
            : undefined
        }
      />
    </Card>
  );
}
